import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from './BreadCrumbs';
import { EventType, useAnalytics } from '../ui/Analytics';
import { SuccessIcon, CancelIcon } from '../assets/svg/StatusIcon';
import {
  useProductName,
  contactInfoRequest,
  useraccountDetailsRequest,
  addressInfoRequest,
  statementPrefRequest,
  updateStatementPrefRequest,
  RootState,
  displayBusinessNameRequest,
  phoneInfoRequest,
  emailInfoRequest,
  useSuppressBusinessName,
  fetchPopupStatusRequest,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import CommanModal from './CommanModal';
import Spinner from './Spinner';
import { logBrazeCustomEvent } from '../config/braze';

const ProfileEdit = lazy(() => import('../screens/MyPofile/index'));

interface PhoneNumber {
  type: string;
  value?: string;
}

interface AvailableCard {
  customerType: string;
  nameOnCard: string;
  customerId: string;
  cardNumber: string;
}

interface Address {
  country: {
    code: string;
    description: string;
  };
  city: string;
  postalCode: string;
  line1: string;
  line2: string;
}

interface CorrespondenceLanguage {
  code: string;
  description: string;
}
interface ContactDetails {
  id: number;
  emailAddress: string;
  phoneNumber: PhoneNumber[];
  address: Address;
  correspondenceLanguage: CorrespondenceLanguage;
  availableCards: AvailableCard[];
  statusCode: string;
}

const MyProfile = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountId, customerId, accountDetails, availableCards } = useSelector(
    (state: any) => state.user
  );
  const {
    loading: popupLoading,
    isWalletEnabled,
    isUserCanAddCard,
    NotRemindMeLater,
    status: popupStatus,
  } = useSelector((state: RootState) => state.popup);
  console.log('popup data', popupStatus);

  useEffect(() => {
    if (accountId && popupStatus == null) {
      dispatch(fetchPopupStatusRequest({ accountId, customerId }));
    }
  }, [accountId, customerId, dispatch]);

  useEffect(() => {
    logBrazeCustomEvent('viewed_my_profile');
  }, []);

  const { cardStructure, productName } = useProductName();

  const {
    contactDetails,
    optionError,
    statementPref,
    updateStatementPref,
    loading,
    error,
  } = useSelector((state: any) => state.contactInfo);
  const { entries, assets } = contentfulData;
  const reCaptchaErrors = entries?.rb_error?.recaptchaErrors;
  const breadcrumbs = [
    { name: entries?.rb_navigation?.homeLogoTitle, href: '/home' },
    { name: entries?.rb_navigation?.moreMenu?.my_profile, href: '/my-profile' },
  ];

  const featureFlaggingData = entries?.rb_feature_flagging?.featureFlagging;
  const prefLangList = ['English', 'Française'];
  const profileWeb = entries?.rb_web_profile?.profileWebWelcome;
  const [selectedEditForm, setSelectedEditForm] = useState('');
  const [isToggled, setIsToggled] = useState(false);
  const hasMounted = useRef(false);
  const [updatedCustomerId, setUpdatedCustomerId] = useState('');
  const [selectedCard, setSelectedCard] = useState<AvailableCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingEditForm, setPendingEditForm] = useState('');
  const getItemsPerPage = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) return 1; // Small screens
    if (screenWidth < 1024) return 2; // Medium screens
    return cardStructure.isHorizontal ? 2 : 3;
  };
  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());
  const [visibleRange, setVisibleRange] = useState({
    start: 0,
    end: itemsPerPage,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalHeadingMessage, setModalHeadingMessage] = useState('');
  const [modalSubMsg, setModalSubMsg] = useState('');
  const [showStatementPrefStatus, setShowStatementPrefStatus] = useState(false);

  const verticalImagesCount = contactDetails?.availableCards.filter(
    (card: AvailableCard) =>
      card.customerType === 'PRIMARY' || card.customerType === 'AUTHORIZED_USER'
  ).length;

  const horizontalImagesCount = contactDetails?.availableCards.filter(
    (card: AvailableCard) =>
      card.customerType === 'PRIMARY' || card.customerType === 'AUTHORIZED_USER'
  ).length;

  useEffect(() => {
    if (!hasMounted.current && contactDetails?.availableCards?.length > 0) {
      setSelectedCard(contactDetails.availableCards[0]);
      setUpdatedCustomerId(contactDetails?.availableCards[0]?.customerId);
      hasMounted.current = true;
    }
  }, [contactDetails]);

  useEffect(() => {
    dispatch(
      addressInfoRequest({
        accountId: accountId,
        customerId: customerId,
      })
    );
  }, []);
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(getItemsPerPage());
    };
    setItemsPerPage(getItemsPerPage());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cardStructure.isHorizontal]);

  useEffect(() => {
    setVisibleRange({ start: 0, end: itemsPerPage });
  }, [itemsPerPage]);

  useEffect(() => {
    dispatch(
      statementPrefRequest({
        accountId: accountId,
        customerId: customerId,
      })
    );
  }, []);

  useEffect(() => {
    if (statementPref) {
      setIsToggled(
        statementPref?.popupResponse?.managePaperStatementFlag || false
      );
    }
  }, [statementPref]);

  useEffect(() => {
    if (updateStatementPref && updateStatementPref?.statusCode === '200') {
      setShowStatementPrefStatus(true);
    }
    return () => setShowStatementPrefStatus(false);
  }, [updateStatementPref]);

  useEffect(() => {
    if (accountId && customerId) {
      if (contactDetails === null && !loading) {
        dispatch(
          contactInfoRequest({
            accountId: accountId,
            primaryCustomerId: customerId,
            customerId: customerId,
          })
        );
      }
    }
  }, [accountId, customerId]);

  const initiateOpenEdit = (modal: string) => {
    setPendingEditForm(modal);
    if (popupStatus?.showActivateCardPopup) {
      setModalHeadingMessage(profileWeb?.modal_popup?.title);
      setModalSubMsg(profileWeb?.modal_popup?.sub_title);
      setIsModalOpen(true);
      return;
    }
    const targetCustomerId = updatedCustomerId || customerId;
    if (modal === 'phone') {
      dispatch(
        phoneInfoRequest({
          accountId: accountId,
          customerId: targetCustomerId,
        })
      );
    } else if (modal === 'address') {
      dispatch(
        addressInfoRequest({
          accountId: accountId,
          customerId: targetCustomerId,
        })
      );
    } else if (modal === 'email') {
      dispatch(
        emailInfoRequest({
          accountId: accountId,
          customerId: targetCustomerId,
        })
      );
    }
  };

  useEffect(() => {
    if (!pendingEditForm) return;
    if (!loading && !optionError) {
      setSelectedEditForm(pendingEditForm);
      setPendingEditForm('');
    } else if (!loading && optionError) {
      setPendingEditForm('');
    }
  }, [loading, optionError, pendingEditForm]);

  // useEffect(() => {
  //   if (contactDetails?.availableCards?.length) {
  //     const primaryCard = contactDetails?.availableCards.find(
  //       (card: AvailableCard) => card.customerType === 'PRIMARY'
  //     );
  //     setSelectedCard(primaryCard || contactDetails?.availableCards[0]);
  //     dispatch(
  //       contactInfoRequest({
  //         accountId,
  //         primaryCustomerId: customerId,
  //         customerId: primaryCard?.customerId || contactDetails?.availableCards[0].customerId,
  //       })
  //     );
  //   }
  // }, [dispatch, accountId, customerId]);
  console.log('profile error', error, reCaptchaErrors);
  useEffect(() => {
    if (error) {
      if (error?.status == 401) {
        setModalHeadingMessage(
          reCaptchaErrors?.my_profile_generic_error?.[error?.errorCode]?.title
        );
        setModalSubMsg(
          reCaptchaErrors?.my_profile_generic_error?.[error?.errorCode]
            ?.description
        );
        setIsModalOpen(true);
      } else {
        setModalHeadingMessage(profileWeb?.modal_popup?.title);
        setModalSubMsg(profileWeb?.modal_popup?.sub_title);
        setIsModalOpen(true);
      }
    }
  }, [error]);

  const primaryCard = contactDetails?.availableCards?.find(
    (card: AvailableCard) => card.customerType === 'PRIMARY'
  );

  const handleCardSelection = (customerId: string) => {
    const selectedCardDetails = contactDetails?.availableCards?.find(
      (card: AvailableCard) => card?.customerId === customerId
    );
    setUpdatedCustomerId(customerId);
    if (selectedCardDetails) {
      setSelectedCard(selectedCardDetails);
      const primaryCustomerId = contactDetails?.availableCards[0]?.customerId;
      dispatch(
        contactInfoRequest({
          accountId: accountId,
          primaryCustomerId: primaryCustomerId,
          customerId: selectedCardDetails?.customerId,
        })
      );
    }
  };

  const isFirstCardSelected = selectedCard?.customerType === 'PRIMARY';

  const handleToggle = () => {
    setIsToggled((prevState) => !prevState);
    dispatch(
      updateStatementPrefRequest({
        accountId: accountId,
        customerId: customerId,
        requestBody: {
          status: !isToggled ? 'ACTIVE' : 'INACTIVE',
        },
      })
    );
  };

  const openEditModal = (modal: string) => {
    setSelectedEditForm(modal || '');
  };

  const closeEditModal = () => {
    setSelectedEditForm('');
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phoneNumber;
  };
  const { displayBusinessName } = useSelector(
    (state: RootState) => state.contactInfo
  );

  useEffect(() => {
    if (accountId) {
      if (
        displayBusinessName == null &&
        accountDetails?.productName === 'Rogers Small Business'
      ) {
        dispatch(displayBusinessNameRequest({ accountId }));
      }
    }
  }, [accountDetails?.productName, accountId, dispatch]);

  const suppressBusinessName = useSuppressBusinessName({
    displayBusinessName,
    entries,
    productName: accountDetails?.productName,
  });

  const handleNext = () => {
    if (visibleRange.end < contactDetails?.availableCards.length) {
      setVisibleRange((prevRange) => ({
        start: prevRange.start + 1,
        end: prevRange.end + 1,
      }));
    }
  };

  const handlePrev = () => {
    if (visibleRange.start > 0) {
      setVisibleRange((prevRange) => ({
        start: prevRange.start - 1,
        end: prevRange.end - 1,
      }));
    }
  };

  const getBgColor = () => {
    if (isToggled && selectedCard?.customerType !== 'PRIMARY') {
      return 'bg-green-100';
    }
    return isToggled ? 'bg-green-500' : 'bg-gray-500';
  };

  const statementPrefrence = profileWeb?.statement_preference?.description;

  const formatName = (name: any) => {
    if (name.length <= 20) return name;
    const splitIndex = name.lastIndexOf(' ', 15);
    if (splitIndex === -1)
      return (
        <>
          {name.slice(0, 15)}
          <br />
          {name.slice(15)}
        </>
      );
    return (
      <>
        {name.slice(0, splitIndex)}
        <br />
        {name.slice(splitIndex + 1)}
      </>
    );
  };

  return (
    <div className="bg-bg-body py-4 px-8">
      {showStatementPrefStatus && (
        <div className="w-full px-[40px] py-[10px] bg-[#1E8240] text-[#FFF] text-[16px] font-[500] leading-[20px] flex justify-between gap-[10px] items-center absolute top-0 left-0">
          <div className="flex gap-[10px]  items-center">
            <div className="flex-shrink-0">
              <SuccessIcon fill="#FFF" />
            </div>
            {
              profileWeb?.statement_preference
                ?.statement_preference_banner_message
            }
          </div>
          <div
            className="flex-shrink-0 ml-0 cursor-pointer"
            onClick={() => setShowStatementPrefStatus(false)}
          >
            <CancelIcon />
          </div>
        </div>
      )}
      <Breadcrumbs items={breadcrumbs} />
      <div className="w-full mx-auto rounded-[15px] bg-white min-h-[70vh] p-6">
        {contactDetails?.availableCards ? (
          <>
            <h1
              aria-label={`${
                suppressBusinessName
                  ? displayBusinessName + ', '
                  : `${profileWeb?.welcome_text} `
              }${
                primaryCard?.preferredName
                  ? primaryCard?.preferredName
                  : primaryCard?.nameOnCard
              }`}
              className="text-[20px] font-[500] md:font-[650] text-[#1F1F1F] mb-4 ml-[5px] text-center md:text-start"
            >
              {suppressBusinessName
                ? displayBusinessName + ', '
                : `${profileWeb?.welcome_text}`}
              {primaryCard?.preferredName
                ? primaryCard?.preferredName
                : primaryCard?.nameOnCard}
            </h1>
            <div
              className={`border border-gray-200 rounded-lg lg:p-4 p-4 mb-6 bg-gray-100 ${cardStructure.isHorizontal ? 'h-[300px] md:h-64 lg:h-48' : 'h-96 md:h-80 lg:h-52'} lg:flex items-start overflow-hidden grid lg:justify-between`}
            >
              <div className="text-center lg:text-left relative">
                {(verticalImagesCount > itemsPerPage ||
                  horizontalImagesCount > itemsPerPage) && (
                  <button
                    className="absolute w-16 mt-6 md:block lg:hidden hidden"
                    onClick={handlePrev}
                  >
                    {visibleRange.start === 0 ? (
                      <img
                        src={assets?.left_arrow_disabled}
                        alt="leftDisabled"
                        role="button"
                      />
                    ) : (
                      <img src={assets?.left_arrow_enabled} alt="leftEnabled" />
                    )}
                  </button>
                )}
                {contactDetails?.availableCards.length === 1 ? (
                  <h2
                    aria-label={`${profileWeb?.card_selection?.title_single_card} ${productName}`}
                    className="text-3xl text-gray-700 lg:mt-12 lg:ml-20"
                  >
                    {profileWeb?.card_selection?.title_single_card}{' '}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: productName || '',
                      }}
                    />
                  </h2>
                ) : (
                  <div
                    className={`${!cardStructure.isHorizontal ? 'lg:mt-10 lg:ml-16' : 'lg:mt-8 lg:ml-16'}`}
                  >
                    <h2
                      className="text-3xl text-gray-700"
                      aria-label={profileWeb?.card_selection?.title_multi_card}
                    >
                      {profileWeb?.card_selection?.title_multi_card}
                    </h2>
                    <h4
                      className="text-lg text-gray-600 lg:my-4 my-2"
                      aria-label={
                        profileWeb?.card_selection?.sub_title_multi_card
                      }
                    >
                      {profileWeb?.card_selection?.sub_title_multi_card}
                    </h4>
                  </div>
                )}
                {(verticalImagesCount > itemsPerPage ||
                  horizontalImagesCount > itemsPerPage) && (
                  <button
                    className="absolute right-1 top-[1px] mt-6 w-16 lg:hidden hidden md:block"
                    onClick={handleNext}
                  >
                    {visibleRange.end >=
                    contactDetails?.availableCards.length ? (
                      <img
                        src={assets?.right_arrow_disabled}
                        alt="rightDisabled"
                        role="button"
                      />
                    ) : (
                      <img
                        src={assets?.right_arrow_enabled}
                        alt="rightEnabled"
                      />
                    )}
                  </button>
                )}
              </div>
              <div
                className={`relative mt-5 lg:mt-0 justify-center lg:gap-6 gap-4 flex lg:flex-nowrap flex-wrap ${!cardStructure.isHorizontal && contactDetails?.availableCards?.length <= 2 ? 'lg:mr-64' : ''} ${cardStructure.isHorizontal && contactDetails?.availableCards?.length === 1 ? 'lg:mr-60' : ''}`}
              >
                {(verticalImagesCount > itemsPerPage ||
                  horizontalImagesCount > itemsPerPage) && (
                  <button
                    className={`relative left-0 w-16 lg:block hidden ${cardStructure.isHorizontal ? '' : 'bottom-16'}`}
                    onClick={handlePrev}
                  >
                    {visibleRange.start === 0 ? (
                      <img
                        src={assets?.left_arrow_disabled}
                        alt="leftDisabled"
                      />
                    ) : (
                      <img src={assets?.left_arrow_enabled} alt="leftEnabled" />
                    )}
                  </button>
                )}

                {contactDetails?.availableCards
                  ?.slice(visibleRange.start, visibleRange.end)
                  ?.map((card: AvailableCard, index: any) => {
                    const imageSourceVertical =
                      card.customerType === 'PRIMARY'
                        ? assets?.vertical_primary
                        : assets?.vertical_supplementary;
                    const imageSourceHorizontal =
                      card.customerType === 'PRIMARY'
                        ? assets?.horizontal_primary
                        : assets?.horizontal_supplementary;
                    const imageSelectedHorizontal =
                      card.customerType === 'PRIMARY'
                        ? assets?.horizontal_primary_selected
                        : assets?.horizontal_secondary_selected;
                    const imageSelectedVertical =
                      card.customerType === 'PRIMARY'
                        ? assets?.vertical_primary_selected
                        : assets?.vertical_secondary_selected;
                    const imageSourceElite =
                      card.customerType === 'PRIMARY' &&
                      (accountDetails?.productName === 'World Elite' ||
                        accountDetails?.productName ===
                          'Rogers Small Business' ||
                        accountDetails?.productName === 'Legend Mastercard')
                        ? assets?.vertical_primary_elite
                        : assets?.vertical_supplementary_elite;
                    const imageSourceSelectedElite =
                      card.customerType === 'PRIMARY' &&
                      (accountDetails?.productName === 'World Elite' ||
                        accountDetails?.productName ===
                          'Rogers Small Business' ||
                        accountDetails?.productName === 'Legend Mastercard')
                        ? assets?.vertical_primarySelected_elite
                        : assets?.vertical_supplementarySelected_elite;

                    const showCard = cardStructure.isHorizontal
                      ? imageSourceHorizontal
                      : imageSourceVertical;

                    if (!showCard) return null;

                    const isSelected =
                      selectedCard?.cardNumber === card.cardNumber;

                    const isEliteProduct =
                      accountDetails?.productName === 'World Elite' ||
                      accountDetails?.productName === 'Rogers Small Business' ||
                      accountDetails?.productName === 'Legend Mastercard';

                    let imageSrc;
                    if (isSelected) {
                      if (cardStructure.isHorizontal) {
                        imageSrc = imageSelectedHorizontal;
                      } else {
                        imageSrc = isEliteProduct
                          ? imageSourceSelectedElite
                          : imageSelectedVertical;
                      }
                    } else if (cardStructure.isHorizontal) {
                      imageSrc = imageSourceHorizontal;
                    } else {
                      imageSrc = isEliteProduct
                        ? imageSourceElite
                        : imageSourceVertical;
                    }

                    return (
                      <div
                        key={index}
                        className={`relative ${isSelected ? '' : 'mt-12'} cursor-pointer`}
                        aria-labelled={`card-${card.cardNumber}`}
                        onClick={() => handleCardSelection(card?.customerId)}
                      >
                        <img
                          src={imageSrc}
                          className={`${cardStructure.isHorizontal ? 'lg:w-96 w-72' : 'w-56'} h-auto flex-shrink-0`}
                          alt="cardSelection"
                        />
                        <p
                          className={`text-white text-sm absolute left-[15%] ${cardStructure.isHorizontal ? 'top-[30%]' : 'top-[23%]'}`}
                          aria-live="polite"
                        >
                          {card.cardNumber.slice(-10)}
                          <br />
                          {formatName(card.nameOnCard)}
                        </p>
                      </div>
                    );
                  })}
                {(verticalImagesCount > itemsPerPage ||
                  horizontalImagesCount > itemsPerPage) && (
                  <button
                    className={`relative right-0 w-16 hidden lg:block ${cardStructure.isHorizontal ? '' : 'bottom-16'}`}
                    onClick={handleNext}
                  >
                    {visibleRange.end >=
                    contactDetails?.availableCards.length ? (
                      <img
                        src={assets?.right_arrow_disabled}
                        alt="rightDisabled"
                      />
                    ) : (
                      <img
                        src={assets?.right_arrow_enabled}
                        alt="rightEnabled"
                      />
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="sm:block md:hidden text-center mb-4">
              {contactDetails?.availableCards.length > 1 && (
                <>
                  <button className="relative w-16" onClick={handlePrev}>
                    {visibleRange.start === 0 ? (
                      <img
                        src={assets?.left_arrow_disabled}
                        alt="leftDisabled"
                      />
                    ) : (
                      <img src={assets?.left_arrow_enabled} alt="leftEnabled" />
                    )}
                  </button>
                  <button className="relative w-16" onClick={handleNext}>
                    {visibleRange.end >=
                    contactDetails?.availableCards.length ? (
                      <img
                        src={assets?.right_arrow_disabled}
                        alt="rightDisabled"
                      />
                    ) : (
                      <img
                        src={assets?.right_arrow_enabled}
                        alt="rightEnabled"
                      />
                    )}
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-wrap lg:flex-nowrap">
              <div>
                <div className="w-full md:w-full">
                  <div className="border border-gray-200 rounded-lg p-6 mb-5 lg:mr-4">
                    <h4
                      className="font-medium text-gray-700 mb-4"
                      aria-label={profileWeb?.statement_preference?.title}
                    >
                      {renderDescriptionParagraphs(
                        profileWeb?.statement_preference?.title
                      )}
                    </h4>
                    <div className="flex justify-between items-center my-4">
                      <p className="text-sm text-gray-600 flex-1 pr-10">
                        {isToggled
                          ? statementPrefrence
                              .replace(
                                '%s',
                                profileWeb?.statement_preference?.paper
                              )
                              .replace(
                                '%f',
                                profileWeb?.statement_preference?.electronic
                              )
                          : statementPrefrence
                              .replace(
                                '%s',
                                profileWeb?.statement_preference?.electronic
                              )
                              .replace(
                                '%f',
                                profileWeb?.statement_preference?.paper
                              )}
                      </p>
                      <button
                        disabled={selectedCard?.customerType !== 'PRIMARY'}
                        aria-label="statement"
                        onClick={handleToggle}
                        className={`relative inline-flex items-center h-[30px] rounded-full w-[51px] transition-all duration-300 ease-in-out ${getBgColor()}`}
                      >
                        <span
                          className={`absolute inline-block w-[50%] h-[24px] transform bg-white rounded-full transition-transform duration-300 ease-in-out ${isToggled ? 'translate-x-6' : 'translate-x-0 left-[1px]'}`}
                        ></span>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">
                      {profileWeb?.statement_preference?.terms_and_conditions1}{' '}
                      <a
                        className="text-red-500 underline hover: cursor-pointer"
                        aria-label={
                          profileWeb?.statement_preference
                            ?.terms_and_conditions2
                        }
                        onClick={() => {
                          window.open(
                            entries?.rb_links?.rbLinksCollection
                              ?.statement_preferences_pdf,
                            '_blank'
                          );
                        }}
                      >
                        {
                          profileWeb?.statement_preference
                            ?.terms_and_conditions2
                        }
                      </a>{' '}
                      {profileWeb?.statement_preference?.terms_and_conditions3}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col md:grid md:grid-cols-2 lg:flex lg:flex-row">
                  <div className="flex flex-col lg:flex-row lg:w-full md:mr-4">
                    <div className="border border-gray-200 rounded-lg p-6 mb-4 lg:w-2/3 lg:h-fit lg:mr-4">
                      <div className="flex items-center">
                        <h4
                          className="font-medium text-gray-700 mb-4"
                          aria-label={profileWeb?.address_type?.title1}
                        >
                          {profileWeb?.address_type?.title1}
                        </h4>
                        {pendingEditForm === 'address' && loading ? (
                          <div className="animate-spin w-6 h-6 ml-auto mb-3">
                            <img src={assets?.loader} alt="loader" />
                          </div>
                        ) : isFirstCardSelected && !optionError ? (
                          <img
                            src={assets?.edit_info}
                            className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                            alt="editWebIcon"
                            aria-label="edit"
                            onClick={() => initiateOpenEdit('address')}
                          />
                        ) : null}
                      </div>
                      <p
                        className="text-gray-600"
                        aria-label={contactDetails?.address?.line1}
                      >
                        {contactDetails?.address?.line1}
                        <br />
                        {contactDetails?.address?.line2 && (
                          <>
                            {contactDetails?.address?.line2}
                            <br />
                          </>
                        )}
                        {contactDetails?.address?.city}
                        {', '}
                        {contactDetails?.address?.areaLevelOne?.code}{' '}
                        {contactDetails?.address?.postalCode}
                        <br />
                        {contactDetails?.address?.country?.code}
                      </p>
                    </div>

                    <div
                      className={`border border-gray-200 rounded-lg p-6 mb-4 lg:w-full ${contactDetails?.phoneNumber?.some((phone: any) => phone.value) ? '' : 'h-28'}`}
                    >
                      <div className="flex items-center">
                        <h4
                          className="font-medium text-gray-700 mb-4"
                          aria-label={profileWeb?.phone_number_type?.main_title}
                        >
                          {profileWeb?.phone_number_type?.main_title}
                        </h4>
                        {pendingEditForm === 'phone' && loading ? (
                          <div className="animate-spin w-6 h-6 ml-auto mb-3">
                            <img src={assets?.loader} alt="loader" />
                          </div>
                        ) : (
                          !optionError && (
                            <img
                              src={assets?.edit_info}
                              className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                              alt="editWebIcon"
                              aria-label="edit"
                              onClick={() => initiateOpenEdit('phone')}
                            />
                          )
                        )}
                      </div>
                      <ul className="text-gray-600 space-y-4">
                        {contactDetails?.phoneNumber?.some(
                          (phone: any) => phone.value
                        ) ? (
                          contactDetails?.phoneNumber?.map(
                            (phone: any, index: any) => {
                              const hasValue = phone.value && phone.type;
                              const phoneTypeMap: Record<string, string> = {
                                ALTERNATE_1:
                                  profileWeb?.phone_number_type?.mobile_text,
                                ALTERNATE_2:
                                  profileWeb?.phone_number_type?.other_text,
                                HOME: profileWeb?.phone_number_type?.home_text,
                                BUSINESS:
                                  profileWeb?.phone_number_type?.business_text,
                              };

                              const phoneTypeLabel = hasValue
                                ? phoneTypeMap[phone.type] || phone.type
                                : null;

                              return (
                                hasValue &&
                                phoneTypeLabel && (
                                  <li
                                    key={index}
                                    className={`flex justify-between ${index !== contactDetails?.phoneNumber?.length - 1 ? 'border-b border-gray-200 pb-4' : ''}`}
                                  >
                                    <span aria-label={phoneTypeLabel}>
                                      {phoneTypeLabel}
                                    </span>
                                    <span
                                      aria-label={formatPhoneNumber(
                                        phone.value
                                      )}
                                    >
                                      {formatPhoneNumber(phone.value)}
                                    </span>
                                  </li>
                                )
                              );
                            }
                          )
                        ) : (
                          <li
                            className="text-gray-600"
                            aria-label={
                              profileWeb?.phone_number_type?.add_phone_title
                            }
                          >
                            {profileWeb?.phone_number_type?.add_phone_title}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col lg:hidden md:block">
                    <div className="border border-gray-200 rounded-lg p-6 mb-4">
                      <div className="flex items-center">
                        <h4
                          className="font-medium text-gray-700 mb-4"
                          aria-label={profileWeb?.email_type?.title1}
                        >
                          {profileWeb?.email_type?.title1}
                        </h4>
                        {pendingEditForm === 'email' && loading ? (
                          <div className="animate-spin w-6 h-6 ml-auto mb-3">
                            <img src={assets?.loader} alt="loader" />
                          </div>
                        ) : (
                          !optionError && (
                            <img
                              src={assets?.edit_info}
                              className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                              alt="editWebIcon"
                              aria-label="edit"
                              onClick={() => initiateOpenEdit('email')}
                            />
                          )
                        )}
                      </div>
                      <p
                        className="text-gray-600 break-words"
                        aria-label={
                          contactDetails?.emailAddress
                            ? contactDetails.emailAddress
                                .charAt(0)
                                .toUpperCase() +
                              contactDetails.emailAddress.slice(1).toLowerCase()
                            : profileWeb?.email_type?.add_email_title
                        }
                      >
                        {contactDetails?.emailAddress
                          ? contactDetails.emailAddress
                              .charAt(0)
                              .toUpperCase() +
                            contactDetails.emailAddress.slice(1).toLowerCase()
                          : profileWeb?.email_type?.add_email_title}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-6 mb-4">
                      <div className="flex items-center">
                        <h4
                          className="font-medium text-gray-700 mb-4"
                          aria-label={profileWeb?.language_type?.title1}
                        >
                          {profileWeb?.language_type?.title1}
                        </h4>
                        {pendingEditForm === 'language' && loading ? (
                          <div className="animate-spin w-6 h-6 ml-auto mb-3">
                            <img src={assets?.loader} alt="loader" />
                          </div>
                        ) : isFirstCardSelected && !optionError ? (
                          <img
                            src={assets?.edit_info}
                            className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                            alt="editWebIcon"
                            aria-label="edit"
                            onClick={() => openEditModal('language')}
                          />
                        ) : null}
                      </div>
                      <p
                        className="text-gray-600"
                        aria-label={
                          contactDetails?.correspondenceLanguage?.description.split(
                            ' '
                          )[1] === prefLangList[0]
                            ? prefLangList[0]
                            : prefLangList[1]
                        }
                      >
                        {contactDetails?.correspondenceLanguage?.description.split(
                          ' '
                        )[1] === prefLangList[0]
                          ? prefLangList[0]
                          : prefLangList[1]}
                      </p>
                    </div>
                    {isFirstCardSelected && (
                      <div className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center">
                          <h4
                            className="font-medium text-gray-700 mb-4"
                            aria-label={profileWeb?.settings_type?.title1}
                          >
                            {profileWeb?.settings_type?.title1}
                          </h4>
                          {pendingEditForm === 'password' && loading ? (
                            <div className="animate-spin w-6 h-6 ml-auto mb-3">
                              <img src={assets?.loader} alt="loader" />
                            </div>
                          ) : featureFlaggingData?.profile?.change_password ? (
                            <img
                              src={assets?.edit_info}
                              className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                              alt="editWebIcon"
                              aria-label="edit"
                              color="black"
                              onClick={() => openEditModal('password')}
                            />
                          ) : (
                            <></>
                          )}
                        </div>
                        <p className="text-gray-600">{'******'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-1/3 relative hidden lg:block">
                <div className="border border-gray-200 rounded-lg p-6 mb-4">
                  <div className="flex items-center">
                    <h4
                      className="font-medium text-gray-700 mb-4"
                      data-testid="email-section"
                      aria-label={profileWeb?.email_type?.title1}
                    >
                      {profileWeb?.email_type?.title1}
                    </h4>
                    {pendingEditForm === 'email' && loading ? (
                      <div className="animate-spin w-6 h-6 ml-auto mb-3">
                        <img src={assets?.loader} alt="loader" />
                      </div>
                    ) : (
                      !optionError && (
                        <img
                          src={assets?.edit_info}
                          className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                          alt="editWebIcon"
                          aria-label="edit"
                          onClick={() => initiateOpenEdit('email')}
                        />
                      )
                    )}
                  </div>
                  <p
                    className="text-gray-600 break-words"
                    aria-label={
                      contactDetails?.emailAddress
                        ? contactDetails.emailAddress.charAt(0).toUpperCase() +
                          contactDetails.emailAddress.slice(1).toLowerCase()
                        : profileWeb?.email_type?.add_email_title
                    }
                  >
                    {contactDetails?.emailAddress
                      ? contactDetails.emailAddress.charAt(0).toUpperCase() +
                        contactDetails.emailAddress.slice(1).toLowerCase()
                      : profileWeb?.email_type?.add_email_title}
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 mb-4">
                  <div
                    className="flex items-center"
                    data-testid="language-section"
                  >
                    <h4
                      className="font-medium text-gray-700 mb-4"
                      aria-label={profileWeb?.language_type?.title1}
                    >
                      {profileWeb?.language_type?.title1}
                    </h4>
                    {pendingEditForm === 'language' && loading ? (
                      <div className="animate-spin w-6 h-6 ml-auto mb-3">
                        <img src={assets?.loader} alt="loader" />
                      </div>
                    ) : isFirstCardSelected && !optionError ? (
                      <img
                        src={assets?.edit_info}
                        className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                        alt="editWebIcon"
                        aria-label="edit"
                        onClick={() => openEditModal('language')}
                      />
                    ) : null}
                  </div>
                  <p
                    className="text-gray-600"
                    aria-label={
                      contactDetails?.correspondenceLanguage?.description.split(
                        ' '
                      )[1] === prefLangList[0]
                        ? prefLangList[0]
                        : prefLangList[1]
                    }
                  >
                    {contactDetails?.correspondenceLanguage?.description.split(
                      ' '
                    )[1] === prefLangList[0]
                      ? prefLangList[0]
                      : prefLangList[1]}
                  </p>
                </div>
                {isFirstCardSelected && (
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div
                      className="flex items-center"
                      data-testid="settings-section"
                    >
                      <h4
                        aria-label={profileWeb?.settings_type?.title1}
                        className="font-medium text-gray-700 mb-4"
                      >
                        {profileWeb?.settings_type?.title1}
                      </h4>
                      {pendingEditForm === 'password' && loading ? (
                        <div className="animate-spin w-6 h-6 ml-auto mb-3">
                          <img src={assets?.loader} alt="loader" />
                        </div>
                      ) : featureFlaggingData?.profile?.change_password ? (
                        <img
                          src={assets?.edit_info}
                          className="w-6 h-6 ml-auto mb-3 cursor-pointer"
                          alt="editWebIcon"
                          aria-label="edit"
                          onClick={() => openEditModal('password')}
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                    <p className="text-gray-600">{'******'}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center h-[80vh]">
            <Spinner aria-label="Loading profile" />
          </div>
        )}
      </div>
      {(isModalOpen || selectedEditForm) && (
        <CommanModal
          isOpen={isModalOpen}
          jsxContent={
            <>
              <div
                className="mt-[20px] text-lg font-semibold text-center"
                aria-label={modalHeadingMessage}
              >
                {modalHeadingMessage}
              </div>
              <div aria-label={modalSubMsg} className="mt-[20px]">
                {modalSubMsg}
              </div>
            </>
          }
          onClose={() => {
            if (selectedEditForm) {
              closeEditModal();
            }
            setIsModalOpen(false);
          }}
          buttonText={profileWeb?.modal_popup?.button_text}
          cardStatus={true}
          closeIcon={false}
        />
      )}
      <Suspense fallback={<Spinner />}>
        <ProfileEdit
          selectedEditForm={selectedEditForm}
          closeModal={closeEditModal}
          isPrimary={isFirstCardSelected}
          updatedCustomerId={updatedCustomerId}
        />
      </Suspense>
    </div>
  );
};

export default MyProfile;
